import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Sprout, ArrowLeft, Loader2, KeyRound, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD =
  "w-full rounded-xl border border-[#D8D3C5] bg-white px-4 py-3 text-sm sm:text-base text-[#1A251E] outline-none transition-colors duration-300 focus:border-[#1A382B] placeholder:text-[#6F7E73]";
const LABEL = "block mb-1.5 font-jbm text-xs tracking-wider uppercase text-[#5A826B]";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.new_password !== pw.confirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: pw.new_password });
      localStorage.setItem("kga_forum_token", data.token);
      setDone(true);
      toast.success("Passwort geändert", { description: "Sie sind jetzt angemeldet." });
      setTimeout(() => (window.location.href = "/forum"), 1800);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Zurücksetzen fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1A251E]" data-testid="reset-page">
      <header className="border-b border-[#D8D3C5] bg-[#F8F7F2]/90 backdrop-blur-md">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="reset-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1A382B] text-[#F8F7F2]">
              <Sprout size={18} strokeWidth={1.8} />
            </span>
            <span className="font-display text-xl font-bold text-[#1A382B]">Schönwald · Gärtner-Forum</span>
          </a>
          <a href="/forum" data-testid="reset-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#47544A] hover:text-[#C85A32] transition-colors">
            <ArrowLeft size={15} /> Zum Forum
          </a>
        </div>
      </header>
      <main className="mx-auto max-w-md px-4 sm:px-6 py-14">
        {done ? (
          <div className="rounded-2xl border border-[#5A826B] bg-white p-8 text-center shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="reset-success-card">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
              <CheckCircle2 size={26} />
            </span>
            <h1 className="mt-4 font-display text-2xl font-bold text-[#1A382B]">Passwort geändert!</h1>
            <p className="mt-3 text-sm text-[#47544A]">Sie werden gleich ins Forum weitergeleitet …</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-[#D8D3C5] bg-white p-8 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="reset-form">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B] mb-4">
              <KeyRound size={20} />
            </span>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A382B]">Neues Passwort vergeben</h1>
            <p className="mt-2 mb-6 text-sm leading-relaxed text-[#47544A]">
              Wählen Sie ein neues Passwort für Ihr Forum-Konto (mindestens 8 Zeichen).
            </p>
            {!token && (
              <p className="mb-4 rounded-xl bg-[#B93829]/10 px-4 py-3 text-sm font-medium text-[#B93829]" data-testid="reset-no-token">
                Der Link ist unvollständig. Bitte fordern Sie im Forum einen neuen Link an.
              </p>
            )}
            {error && (
              <p className="mb-4 rounded-xl bg-[#B93829]/10 px-4 py-3 text-sm font-medium text-[#B93829]" data-testid="reset-error">
                {error}
              </p>
            )}
            <label className={LABEL}>Neues Passwort *</label>
            <input type="password" required minLength={8} className={FIELD} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="reset-new-input" />
            <div className="mt-4">
              <label className={LABEL}>Neues Passwort wiederholen *</label>
              <input type="password" required className={FIELD} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="reset-confirm-input" />
            </div>
            <button type="submit" disabled={loading || !token} data-testid="reset-submit-button" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Passwort speichern
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
